import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { loginUser, selectUserInfo, selectAuthLoading, selectAuthError, clearAuthError, clearSignupMessage } from '../Redux/slices/authSlice'; // Adjust path as needed
import { authService } from '../Common/services/httpClient';
import { toast } from 'react-toastify';


const Login = () => {
    const [formData, setFormData] = useState({
        email: '',
        password: '',
    });
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation(); // To potentially receive state from navigate


    const userInfo = useSelector(selectUserInfo);
    const loading = useSelector(selectAuthLoading);
    const error = useSelector(selectAuthError);
    const signupMessageFromState = useSelector(state => state.auth.signupMessage);

    const [displayableSignupMessage, setDisplayableSignupMessage] = useState(null);

    useEffect(() => {
        if (signupMessageFromState) {
            setDisplayableSignupMessage(signupMessageFromState);
            dispatch(clearSignupMessage()); // Clear from Redux store once captured for display
        }
    }, [signupMessageFromState, dispatch]);

    useEffect(() => {
        if (error) {
            // Ensure error is a string before displaying.
            // The httpservice change should make `error` a more direct message.
            const errorMessage = typeof error === 'string' ? error : JSON.stringify(error);
            toast.error(errorMessage);
            // We don't dispatch clearAuthError here immediately to allow the user to see the error
            // It will be cleared on the next submission attempt or when the component unmounts.
        }
    }, [error]); // Only re-run if the error object itself changes

    useEffect(() => {
        if (userInfo && authService.getAccessToken()) {
            navigate('/');
        }
        // Clear any previous auth errors when component mounts.
        // Signup message is handled separately to ensure it's displayed once.
        return () => {
            dispatch(clearAuthError());
        };
    }, [userInfo, navigate, dispatch]); // Removed location from deps as it's not directly used for this effect's logic

    const handleSubmit = async (event) => {
        event.preventDefault();
        // setFormMessage('');
        dispatch(clearAuthError()); // Clear previous errors before new submission


        const { email, password } = formData;
        dispatch(loginUser({ email, password }));
    };

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };


    return (
        <div className="dark:bg-gray-950 flex min-h-screen items-center justify-center bg-gradient-to-br from-[#0f0f1a] to-[#1a1a2e] text-white  p-12">
            <form onSubmit={handleSubmit}>
                <div className="max-w-sm rounded-3xl border border-blue-500 bg-gray-100 p-px dark:from-gray-800 dark:to-transparent">
                    <div className="bg-theme-primary rounded-[calc(1.5rem-1px)] p-12 px-10 dark:bg-gray-900">
                        <div className="flex flex-col items-center">
                            {/* <img className='w-32 h-24 object-contain' src="/Z-Ninth.png" alt='Zyntegration' /> */}
                            <h1 className="dark:text-white  text-xl font-semibold text-white mt-3">Signin to your account</h1>
                            {displayableSignupMessage && !error && <p className="mt-2 text-sm text-green-600">{displayableSignupMessage}</p>}
                        </div>

                        <div className="mt-8 space-y-8">
                            <div className="space-y-6">
                                <input
                                    className="dark:text-white w-full rounded-md border border-gray-300 bg-transparent px-3 py-2 text-sm text-gray-600 placeholder-gray-600 dark:border-gray-700 dark:placeholder-gray-300"
                                    placeholder="Your Email"
                                    type="email"
                                    name="email"
                                    id="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    required
                                    disabled={loading}
                                />

                                <input
                                    className="dark:text-white w-full rounded-md border border-gray-300 bg-transparent px-3 py-2 text-sm text-gray-600 placeholder-gray-600 dark:border-gray-700 dark:placeholder-gray-300"
                                    placeholder="Your Password"
                                    type="password"
                                    name="password"
                                    id="password"
                                    value={formData.password}
                                    onChange={handleChange}
                                    required
                                    disabled={loading}
                                />


                            </div>

                            <button
                                type="submit"
                                className="w-full rounded-lg bg-orange-500 px-12 py-3 font-semibold text-white-500 outline-none transition-all hover:shadow-orange-md disabled:opacity-50 lg:px-16 lg:py-4"
                                disabled={loading}
                            >
                                {loading ? 'Signing in...' : 'Sign In'}
                            </button>

                            <div onClick={() => { if (!loading) navigate('/signup'); }} className={`flex cursor-pointer justify-center text-blue-400 hover:text-blue-500 ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}>
                                Don't have an account? Signup now.
                            </div>

                        </div>
                    </div>
                </div>
            </form>
        </div>
    );
};

export default Login;